import { useNavigate, useRouterState } from '@tanstack/react-router';
import { useState } from 'react';

import { useUI } from '@apps/web/context/ui_provider';
import {
    completeAgentChecklistStep,
    useAgentChecklist,
} from '@apps/web/features/learn_phoebe/api/agent_checklist';
import { canShowGetStartedChecklist } from '@apps/web/features/learn_phoebe/model/access';
import {
    type ChecklistStep,
    checklistStepTask,
    GET_STARTED_STEPS,
} from '@apps/web/features/learn_phoebe/model/checklist_steps';
import { promptPlainText } from '@apps/web/features/learn_phoebe/model/learn_content';
import { useLearnPhoebeAnalytics } from '@apps/web/features/learn_phoebe/model/use_learn_phoebe_analytics';
import { useOrganizationId } from '@apps/web/hooks/use_organization_id';
import { getCalloutDetailOutreachId } from '@apps/web/lib/chat_access';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

type GetStartedChecklistController = {
    // False while loading, for ineligible orgs, and once every step is done —
    // the widget never returns for that scheduler in that organization.
    readonly isVisible: boolean;
    readonly completedStepIds: ReadonlySet<string>;
    readonly completedCount: number;
    readonly isOpen: boolean;
    readonly selectedStep: ChecklistStep;
    // Changes on every selection, so re-picking the open step replays its demo
    // instead of doing nothing — the same contract the page has.
    readonly demoKey: string;
    readonly setIsOpen: (isOpen: boolean) => void;
    readonly selectStep: (stepId: string) => void;
    readonly tryStep: (step: ChecklistStep) => void;
    // The dialog shows a subset of the full task list; this hands the reader
    // off to the page rather than dead-ending them.
    readonly openLearnPhoebePage: () => void;
};

export function useGetStartedChecklist(): GetStartedChecklistController {
    const organizationId = useOrganizationId();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'get-started-checklist',
    });
    // The condition that decides whether the widget can ever render also gates
    // the request: the checklist GET derives completion from run history, so an
    // org that will never see the widget must not pay for it.
    const isEligible = canShowGetStartedChecklist(organization);
    const {
        data: checklist,
        isLoading,
        mutate,
    } = useAgentChecklist(organizationId, {
        enabled: isEligible,
        reason: 'get-started-checklist',
    });
    const { openChatWithComposerText } = useUI();
    const {
        captureTaskOpened,
        captureTryInChatClicked,
        captureChecklistOpened,
    } = useLearnPhoebeAnalytics();
    const navigate = useNavigate();
    const pathname = useRouterState({
        select: (state) => state.location.pathname,
    });
    const [isOpen, setIsOpen] = useState(false);
    // undefined = follow the first incomplete step; a step id = selected.
    const [activeStepId, setActiveStepId] = useState<string | undefined>(
        undefined,
    );
    const [playbackRun, setPlaybackRun] = useState(0);

    const completedStepIds = new Set(checklist?.completed_step_ids ?? []);
    const completedCount = GET_STARTED_STEPS.filter((step) =>
        completedStepIds.has(step.id),
    ).length;
    const firstIncompleteStep =
        GET_STARTED_STEPS.find((step) => !completedStepIds.has(step.id)) ??
        GET_STARTED_STEPS[0]!;
    const selectedStep =
        GET_STARTED_STEPS.find((step) => step.id === activeStepId) ??
        firstIncompleteStep;
    const isVisible =
        organizationId !== null &&
        isEligible &&
        !isLoading &&
        checklist !== undefined &&
        completedCount < GET_STARTED_STEPS.length;

    function tryStep(step: ChecklistStep): void {
        if (organizationId === null) {
            return;
        }
        // The checklist lives in the sidebar, so it can be opened from a
        // callout detail page — where the docked chat is the outreach thread,
        // not the general agent. Leave that route first, otherwise the
        // general-agent prompt has no composer to land in.
        if (getCalloutDetailOutreachId(pathname) !== null) {
            void navigate({ to: '/learn-phoebe' });
        }
        captureTryInChatClicked({
            surface: 'checklist_dialog',
            taskId: step.taskId,
            stepId: step.id,
        });
        openChatWithComposerText(
            promptPlainText(checklistStepTask(step).prompt),
            'learn_phoebe',
        );
        setIsOpen(false);
        setActiveStepId(undefined);
        // Show the tick immediately rather than waiting on the write.
        void mutate(
            (current) =>
                current === undefined
                    ? current
                    : {
                          ...current,
                          completed_step_ids: Array.from(
                              new Set([...current.completed_step_ids, step.id]),
                          ),
                      },
            { revalidate: false },
        );
        void (async () => {
            try {
                const saved = await completeAgentChecklistStep({
                    organizationId,
                    stepId: step.id,
                });
                // Take the write's own response rather than re-running the GET.
                // A platform admin browsing an org they are not a member of has
                // no profile row, so the write is a no-op returning an empty
                // list — and a blind revalidate would then wipe the tick the
                // user just earned.
                if (saved.completed_step_ids.length > 0) {
                    void mutate(saved, { revalidate: false });
                }
            } catch {
                // Best-effort: the usage-derived GET picks it up on the next
                // load. Deliberately no revalidate here — that would silently
                // un-tick the step the user just clicked.
            }
        })();
    }

    return {
        isVisible,
        completedStepIds,
        completedCount,
        isOpen,
        selectedStep,
        demoKey: `${selectedStep.taskId}-${playbackRun}`,
        setIsOpen: (nextIsOpen: boolean) => {
            setIsOpen(nextIsOpen);
            // The closed→open transition, not the setter call: setIsOpen(true)
            // on an already-open dialog must not inflate the funnel's
            // denominator.
            if (nextIsOpen && !isOpen) {
                captureChecklistOpened({
                    completedCount,
                    totalSteps: GET_STARTED_STEPS.length,
                    // Auto-selected without a click, so it fires no task_opened.
                    defaultStepId: selectedStep.id,
                });
            }
            if (!nextIsOpen) {
                // Reopening should land on the first incomplete step, not
                // whatever was last selected.
                setActiveStepId(undefined);
            }
        },
        selectStep: (stepId: string) => {
            // Re-picking the open step replays its demo; only a switch to a
            // different step is a task open. The page guards the same way, so
            // the two surfaces stay comparable.
            const isOpeningNewStep = stepId !== selectedStep.id;
            setActiveStepId(stepId);
            setPlaybackRun((run) => run + 1);
            const step = GET_STARTED_STEPS.find(
                (candidate) => candidate.id === stepId,
            );
            if (isOpeningNewStep && step !== undefined) {
                captureTaskOpened({
                    surface: 'checklist_dialog',
                    taskId: step.taskId,
                    stepId: step.id,
                });
            }
        },
        tryStep,
        openLearnPhoebePage: () => {
            void navigate({ to: '/learn-phoebe' });
            setIsOpen(false);
            setActiveStepId(undefined);
        },
    };
}
